import { useQuery } from '@tanstack/react-query';
import { type ReactNode, useEffect, useRef, useState } from 'react';
import type { FieldValues, Path, UseFormReturn } from 'react-hook-form';
import type { Design } from '../../../../api/design';
import type { DesignPreset } from '../../../../api/designPreset';
import { designPresetsQuery } from '../../../../api/designPreset';
import type { PastCombination } from '../../../../api/pastCombination';
import { pastCombinationsAvailabilityQuery } from '../../../../api/pastCombination';
import { getPresetFeatures } from '../../../utils/designPreset';
import { DesignPresetChooserDialog } from './DesignPresetChooserDialog';

type DesignFieldValues = FieldValues & { design: Design | null };

/**
 * When the design id changes after mount, fetch its presets and past-order
 * availability, then offer to apply one. Opening an existing item does not
 * prompt. v1 designs are not offered.
 */
export function useDesignPresetPrompt<T extends DesignFieldValues>(
  methods: UseFormReturn<T>,
  onApply: (values: DesignPreset | PastCombination) => void
): ReactNode {
  const design = methods.watch('design' as Path<T>) as Design | null;
  const designId = design?.id ?? null;
  const prevDesignId = useRef<number | null | undefined>(undefined);
  const [promptDesign, setPromptDesign] = useState<Design | null>(null);

  useEffect(() => {
    if (prevDesignId.current === undefined) {
      prevDesignId.current = designId;
      return;
    }
    if (designId === prevDesignId.current) {
      return;
    }
    prevDesignId.current = designId;
    const next =
      designId != null && design && getPresetFeatures(design.vestmentType)
        ? design
        : null;
    setPromptDesign(next);
  }, [design, designId]);

  const promptDesignId = promptDesign?.id ?? null;
  const {
    data: presets,
    isSuccess: presetsSuccess,
    isError: presetsError,
  } = useQuery({
    ...designPresetsQuery(promptDesignId ?? 0),
    enabled: promptDesignId != null,
  });
  const {
    data: pastCombinationPage,
    isSuccess: pastCombinationsSuccess,
    isError: pastCombinationsError,
  } = useQuery({
    ...pastCombinationsAvailabilityQuery(promptDesignId ?? 0),
    enabled: promptDesignId != null,
  });

  const presetsSettled = presetsSuccess || presetsError;
  const pastCombinationsSettled =
    pastCombinationsSuccess || pastCombinationsError;
  const bothSettled = presetsSettled && pastCombinationsSettled;
  const hasPresets = presetsSuccess && (presets?.length ?? 0) >= 1;
  const hasPastCombinations =
    pastCombinationsSuccess && (pastCombinationPage?.total ?? 0) > 0;

  useEffect(() => {
    if (promptDesignId == null || !bothSettled) {
      return;
    }
    if (!hasPresets && !hasPastCombinations) {
      setPromptDesign(null);
    }
  }, [promptDesignId, bothSettled, hasPresets, hasPastCombinations]);

  const open =
    promptDesign != null && bothSettled && (hasPresets || hasPastCombinations);

  if (!promptDesign || !open) {
    return null;
  }

  return (
    <DesignPresetChooserDialog
      open
      design={promptDesign}
      presets={presets ?? []}
      hasPastCombinations={hasPastCombinations}
      onApplyValues={(values) => {
        onApply(values);
        setPromptDesign(null);
      }}
      onDismiss={() => setPromptDesign(null)}
    />
  );
}
